import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { useSearchParams } from 'react-router-dom';
import useChatEvents from '../hooks/useChatEvents';
import { chatSoundEnabled, setChatSoundEnabled, CHAT_SOUND_EVENT } from '../services/chatSound';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import {
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  TextField,
  IconButton,
  Typography,
  Divider,
  Badge,
  Button,
  CircularProgress,
  Tooltip,
  Alert,
  Chip,
  Switch,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import RefreshIcon from "@mui/icons-material/Refresh";
import PhoneIcon from "@mui/icons-material/Phone";
import SearchIcon from "@mui/icons-material/Search";
import TelegramIcon from "@mui/icons-material/Telegram";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import DoneIcon from '@mui/icons-material/Done';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { styled } from "@mui/material/styles";
import api from "./api/api";

const CHAT_MESSAGE_MAX_LENGTH = 2000;

function MessageReceipt({ message }) {
  const pending = String(message.id).startsWith('pending-');
  const viewed = !pending && Boolean(message.read_at);
  const delivered = !pending && Boolean(message.delivered_at);
  const label = pending ? 'Отправляется…' : viewed ? 'Прочитано' : delivered ? 'Доставлено' : 'Отправлено';
  const Icon = pending ? ScheduleIcon : viewed || delivered ? DoneAllIcon : DoneIcon;
  const description = pending ? 'Сообщение ещё отправляется'
    : viewed ? `Сообщение показано клиенту в открытом чате: ${new Date(message.read_at).toLocaleString('ru-RU')}`
    : delivered ? `Сообщение получено кабинетом: ${new Date(message.delivered_at).toLocaleString('ru-RU')}. Просмотр ещё не подтверждён.`
    : 'Сообщение сохранено в чате. Получение клиентом пока не подтверждено. Старые версии приложения не передают точные статусы.';
  return (
    <Tooltip title={description}>
      <Box component="span" role="img" aria-label={label} tabIndex={0}
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, px: 0.65, py: 0.1,
          borderRadius: 1, bgcolor: '#f8fafc', color: viewed ? '#1565c0' : '#64748b', fontSize: '0.7rem' }}>
        <Icon sx={{ fontSize: 16 }} />
        <span>{label}</span>
      </Box>
    </Tooltip>
  );
}

// ── Styled ──

const ChatContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  height: "calc(100vh - 100px)",
  gap: theme.spacing(2),
  minWidth: 0,
  [theme.breakpoints.down("lg")]: {
    gap: theme.spacing(1),
  },
}));
const ChatListPanel = styled(Paper)(({ theme }) => ({
  width: 370,
  flexShrink: 0,
  overflow: "auto",
  borderRadius: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  [theme.breakpoints.down("lg")]: {
    width: 300,
  },
  [theme.breakpoints.down("md")]: {
    width: 250,
  },
}));
const ChatWindow = styled(Paper)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  borderRadius: theme.spacing(2),
}));
const MessagesContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: "auto",
  padding: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));
const MessageBubble = styled(Box)(({ theme, isOwn }) => ({
  maxWidth: "70%",
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(2),
  backgroundColor: isOwn ? theme.palette.primary.main : theme.palette.grey[200],
  color: isOwn ? "white" : "black",
  alignSelf: isOwn ? "flex-end" : "flex-start",
  wordWrap: "break-word",
  [theme.breakpoints.down("lg")]: {
    maxWidth: "86%",
  },
}));
const InputContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
  alignItems: "center",
  [theme.breakpoints.down("lg")]: {
    padding: theme.spacing(1),
  },
}));

// ── Иконка платформы ──

const PLATFORM_ICONS = {
  avito: { label: "Avito", color: "#00AAFF", letter: "A" },
  telegram: { label: "Telegram Bot", color: "#229ED9", icon: TelegramIcon },
  telegram_personal: {
    label: "Telegram",
    color: "#0088CC",
    icon: TelegramIcon,
  },
  whatsapp: { label: "WhatsApp", color: "#25D366", letter: "W" },
  web: { label: "Web", color: "#9E9E9E", letter: "W" },
};

function PlatformBadge({ platform, size = 18 }) {
  const info = PLATFORM_ICONS[platform] || PLATFORM_ICONS.web;
  const Icon = info.icon;
  return (
    <Tooltip title={info.label}>
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: "50%",
          bgcolor: info.color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {Icon ? (
          <Icon sx={{ fontSize: size * 0.7, color: "#fff" }} />
        ) : (
          <Typography
            sx={{
              fontSize: size * 0.6,
              fontWeight: 700,
              color: "#fff",
              lineHeight: 1,
            }}
          >
            {info.letter}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}

// ── Главный компонент ──

function MessengerPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedChat = searchParams.get('chat');
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiDraft, setAiDraft] = useState("");
  const [aiDraftOpen, setAiDraftOpen] = useState(false);
  const [aiDraftChat, setAiDraftChat] = useState(null);
  const [error, setError] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(chatSoundEnabled);
  const [markingAll, setMarkingAll] = useState(false);
  const markingAllRef = useRef(false);
  const [readNotice, setReadNotice] = useState('');
  useEffect(() => {
    const update = () => setSoundEnabled(chatSoundEnabled());
    window.addEventListener(CHAT_SOUND_EVENT, update);
    window.addEventListener('storage', update);
    return () => { window.removeEventListener(CHAT_SOUND_EVENT, update); window.removeEventListener('storage', update); };
  }, []);
  const messagesContainerRef = useRef(null);
  const requestVersion = useRef(0);
  const loadChatsRef = useRef(null);
  const sendingRef = useRef(false);
  const optimisticSequence = useRef(0);
  const selectedChatRef = useRef(null);
  selectedChatRef.current = selectedChat;

  useEffect(() => {
    if (!requestedChat) return undefined;
    let active = true;
    setSelectedChat(null);
    setMessage('');
    if (!/^[1-9][0-9]*$/.test(requestedChat)) {
      setError('Некорректная ссылка на чат');
      return undefined;
    }
    api.get(`/chats/${requestedChat}`).then(({ data }) => {
      if (!active) return;
      setSelectedChat(data);
      if (data.unread_count > 0) api.post(`/chats/${data.id}/read`).catch(() => {});
    }).catch(() => {
      if (active) setError('Не удалось открыть чат из сделки. Проверьте доступ или повторите переход.');
    });
    return () => { active = false; };
  }, [requestedChat]);

  // Пагинация + фильтры
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [totalChats, setTotalChats] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [platformFilter, setPlatformFilter] = useState("all");
  const unreadMessages = chats.reduce(
    (total, chat) => total + Math.max(0, Number(chat.unread_count) || 0),
    0,
  );

  // Когда список чатов обновляется — синхронизируем открытый чат
  useEffect(() => {
    if (!selectedChat || chats.length === 0) return;
    const updated = chats.find(
      (c) => c.id != null && selectedChat.id != null
        ? c.id === selectedChat.id
        : c.chat_id === selectedChat.chat_id && c.platform === selectedChat.platform
    );
    // Всегда обновляем — не только при смене количества сообщений
    if (updated) {
      setSelectedChat(updated);
    }
  }, [chats]); // eslint-disable-line react-hooks/exhaustive-deps

  // Перезагрузка при смене фильтров
  useEffect(() => {
    loadChats();
  }, [page, limit, searchQuery, platformFilter]);

  const chatConnection = useChatEvents('staff', localStorage.getItem('token'),
    () => loadChatsRef.current({ silent: true }));
  useEffect(() => () => { requestVersion.current += 1; }, []);

  useLayoutEffect(() => {
    const container = messagesContainerRef.current;
    if (container) container.scrollTop = container.scrollHeight;
  }, [selectedChat?.id, selectedChat?.chat_id, selectedChat?.messages?.length]);

  // ── API ──

  const loadChats = useCallback(async ({ silent = false } = {}) => {
    const version = ++requestVersion.current;
    try {
      if (!silent) setLoading(true);
      setError(null);
      const params = new URLSearchParams({ page, limit });
      if (searchQuery) params.append("search", searchQuery);
      if (platformFilter !== "all") params.append("platform", platformFilter);

      const res = await api.get(`/chats/?${params}`, { skipLoading: silent });
      if (version !== requestVersion.current) return;
      setChats(res.data.items || []);
      setTotalPages(res.data.pages || 1);
      setTotalChats(res.data.total || 0);
      // A chat opened from a deal may be outside the current page/filter.
      const opened = selectedChatRef.current;
      if (opened?.id && !(res.data.items || []).some(chat => chat.id === opened.id)) {
        const { data } = await api.get(`/chats/${opened.id}`);
        if (version === requestVersion.current && selectedChatRef.current?.id === opened.id) setSelectedChat(data);
      }
    } catch (err) {
      if (version !== requestVersion.current) return;
      console.error("Ошибка загрузки чатов:", err);
      setError("Не удалось загрузить чаты");
      return false;
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  }, [page, limit, searchQuery, platformFilter]);
  loadChatsRef.current = loadChats;

  const markAllRead = async () => {
    if (markingAllRef.current) return;
    markingAllRef.current = true;
    setMarkingAll(true);
    try {
      await api.post('/chats/read-all', null, { params: { platform: platformFilter } });
      setReadNotice('Входящие отмечены прочитанными во всех чатах выбранного канала.');
      window.dispatchEvent(new Event('staff-chat-read'));
      await loadChatsRef.current();
    } catch { setError('Не удалось отметить сообщения прочитанными. Повторите попытку.'); }
    finally { markingAllRef.current = false; setMarkingAll(false); }
  };

  const handleSendMessage = async () => {
    if (!message.trim() || !selectedChat || sendingRef.current) return;
    const sentText = message;
    const targetChat = selectedChat;
    const optimisticId = `pending-${Date.now()}-${++optimisticSequence.current}`;
    sendingRef.current = true;
    try {
      setSending(true);
      // ── Оптимистичное обновление: показываем сообщение сразу ──
      const optimisticMsg = {
        id: optimisticId,
        chat_id: selectedChat.chat_id,
        type: "исходящее",
        content: sentText,
        timestamp: new Date().toISOString(),
        is_read: true,
        is_ai_generated: false,
      };
      setSelectedChat((prev) => ({
        ...prev,
        messages: [...(prev.messages || []), optimisticMsg],
        last_message: sentText,
      }));
      setMessage("");
      // ── Реальная отправка ──
      await api.post("/chats/send", {
        chat_id: selectedChat.chat_id,
        platform: selectedChat.platform || "avito",
        text: sentText,
      });
      // Перезагружаем чаты чтобы получить серверные id сообщений
      await loadChatsRef.current();
    } catch (err) {
      console.error("Ошибка отправки:", err);
      alert("Не удалось отправить сообщение");
      // Откатываем оптимистичное сообщение
      setSelectedChat((prev) => prev?.chat_id === targetChat.chat_id && prev?.platform === targetChat.platform ? ({
        ...prev,
        messages: (prev.messages || []).filter((m) => m.id !== optimisticId),
      }) : prev);
      if (selectedChatRef.current?.chat_id === targetChat.chat_id && selectedChatRef.current?.platform === targetChat.platform) {
        setMessage(current => current || sentText);
      }
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const handleAIResponse = async (targetChat = selectedChat) => {
    if (!targetChat || targetChat.ai_available === false || aiGenerating) return;
    try {
      setAiGenerating(true);
      const msgs = targetChat.messages || [];
      if (msgs.length === 0) {
        alert("Нет сообщений");
        return;
      }
      const aiRes = await api.post(`/chats/${targetChat.id}/ai-draft`);
      setAiDraft(aiRes.data.draft || "");
      setAiDraftChat(targetChat);
      setAiDraftOpen(true);
    } catch (err) {
      console.error("AI error:", err);
      alert(err.response?.data?.detail || "Ошибка AI");
    } finally {
      setAiGenerating(false);
    }
  };

  const discardAIDraft = () => {
    setAiDraftOpen(false);
    setAiDraft("");
    setAiDraftChat(null);
  };

  const sendAIDraft = async () => {
    const text = aiDraft.trim();
    if (!text || !aiDraftChat || aiDraftChat.ai_available === false || sendingRef.current) return;
    sendingRef.current = true;
    try {
      setSending(true);
      await api.post("/chats/send", {
        chat_id: aiDraftChat.chat_id,
        platform: aiDraftChat.platform,
        text,
      });
      discardAIDraft();
      await loadChatsRef.current();
    } catch (err) {
      console.error("Ошибка отправки AI-черновика:", err);
      alert("Не удалось отправить AI-ответ");
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const togglePreference = async (field, action, enabled) => {
    const target = selectedChatRef.current;
    if (!target) return;
    const previous = target[field] ?? (field === 'notify_rental');
    const update = value => {
      setChats(prev => prev.map(c => c.id === target.id ? { ...c, [field]: value } : c));
      setSelectedChat(prev => prev?.id === target.id ? { ...prev, [field]: value } : prev);
    };
    update(enabled);
    try {
      await api.post(`/chats/${target.id}/${action}?enabled=${enabled}`);
    } catch {
      update(previous);
    }
  };
  const handleToggleAI = enabled => togglePreference('ai_auto_reply', 'toggle-ai', enabled);
  const handleToggleNotify = enabled => togglePreference('notify_rental', 'toggle-notify', enabled);

  const handleSelectChat = async (chat) => {
    if (requestedChat) {
      const params = new URLSearchParams(searchParams);
      params.delete('chat');
      setSearchParams(params, { replace: true });
    }
    setSelectedChat(chat);
    if (chat.unread_count > 0) {
      try {
        await api.post(`/chats/${chat.id}/read`);
        setChats((prev) =>
          prev.map((c) =>
            (c.id != null ? c.id === chat.id : c.chat_id === chat.chat_id && c.platform === chat.platform)
              ? { ...c, unread_count: 0 }
              : c
          )
        );
      } catch {}
    }
  };

  const syncAvito = async () => {
    try {
      setLoading(true);
      await api.post("/avito-chats/sync");
      await loadChats();
    } catch (err) {
      setError("Ошибка синхронизации Avito");
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (ts) => {
    if (!ts) return "";
    const d = new Date(ts);
    const now = new Date();
    if (now - d < 86400000) {
      return d.toLocaleTimeString("ru-RU", {
        hour: "2-digit",
        minute: "2-digit",
      });
    }
    return d.toLocaleString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // ── Render ──

  return (
    <Box sx={{ p: { xs: 1, lg: 3 }, minWidth: 0 }}>
      <Box className="erp-section-heading"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="h4" component="h1">
            💬 Мессенджер
          </Typography>
          {unreadMessages > 0 && (
            <Tooltip title={`Новых сообщений: ${unreadMessages}`}>
              <Badge color="error" badgeContent={unreadMessages} max={99}>
                <NotificationsActiveIcon color="warning" />
              </Badge>
            </Tooltip>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <Tooltip title={soundEnabled ? 'Выключить звук новых сообщений' : 'Включить звук новых сообщений в этом браузере'}>
          <IconButton aria-label="Звук новых сообщений" aria-pressed={soundEnabled}
            onClick={() => setChatSoundEnabled(!soundEnabled)}>
            {soundEnabled ? <VolumeUpIcon /> : <VolumeOffIcon />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Все входящие выбранного канала, включая другие страницы. История сохранится.">
          <span><Button onClick={markAllRead} disabled={markingAll}>Прочитать все</Button></span>
        </Tooltip>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={syncAvito}
          disabled={loading}
        >
          {loading ? "Загрузка..." : "Синхронизировать Avito"}
        </Button>
        </Box>
      </Box>

      {['reconnecting', 'unauthorized'].includes(chatConnection) && <Alert severity="warning" sx={{ mb: 2 }}>
        {chatConnection === 'unauthorized' ? 'Сессия чата завершена. Войдите снова.' : 'Связь с чатом прервана. Восстанавливаем соединение…'}
      </Alert>}
      {readNotice && <Alert severity="success" onClose={() => setReadNotice('')} sx={{ mb: 2 }}>{readNotice}</Alert>}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <ChatContainer className="erp-messenger">
        {/* ── Левая панель: список чатов ── */}
        <ChatListPanel className="erp-chat-list" elevation={3}>
          <Tabs
            value={platformFilter}
            onChange={(_, v) => {
              setPlatformFilter(v);
              setPage(1);
            }}
            variant="fullWidth"
            sx={{ borderBottom: 1, borderColor: "divider", minHeight: 40 }}
          >
            <Tab label="Все" value="all" sx={{ minHeight: 40, py: 0 }} />
            <Tab
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      borderRadius: "50%",
                      bgcolor: "#00AAFF",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      sx={{ fontSize: 9, fontWeight: 700, color: "#fff" }}
                    >
                      A
                    </Typography>
                  </Box>
                  Avito
                </Box>
              }
              value="avito"
              sx={{ minHeight: 40, py: 0 }}
            />
            <Tab
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <TelegramIcon sx={{ fontSize: 16, color: "#229ED9" }} />
                  Telegram
                </Box>
              }
              value="telegram"
              sx={{ minHeight: 40, py: 0 }}
            />
            <Tab
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      borderRadius: "50%",
                      bgcolor: "#7C3AED",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      sx={{ fontSize: 9, fontWeight: 700, color: "#fff" }}
                    >
                      W
                    </Typography>
                  </Box>
                  Виджет
                </Box>
              }
              value="web"
              sx={{ minHeight: 40, py: 0 }}
            />
          </Tabs>

          <Box sx={{ p: 1.5, borderBottom: 1, borderColor: "divider" }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Поиск..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ color: "text.secondary", mr: 1 }} />
                ),
              }}
              sx={{ mb: 1 }}
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Чатов: {totalChats}
              </Typography>
              <FormControl size="small" sx={{ minWidth: 70 }}>
                <Select
                  value={limit}
                  onChange={(e) => {
                    setLimit(e.target.value);
                    setPage(1);
                  }}
                >
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          {loading && chats.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <List sx={{ p: 0, flex: 1, overflow: "auto" }}>
              {chats.map((chat) => (
                <React.Fragment key={chat.id}>
                  <ListItem
                    button
                    selected={selectedChat?.id === chat.id}
                    onClick={() => handleSelectChat(chat)}
                    sx={{
                      "&.Mui-selected": { backgroundColor: "action.selected" },
                    }}
                  >
                    <ListItemAvatar>
                      <Badge badgeContent={chat.unread_count} color="error">
                        <Avatar
                          sx={{
                            bgcolor:
                              PLATFORM_ICONS[chat.platform]?.color || "#999",
                            width: 42,
                            height: 42,
                          }}
                        >
                          {chat.platform === "telegram" ? (
                            <TelegramIcon sx={{ fontSize: 22 }} />
                          ) : (
                            <Typography sx={{ fontWeight: 700, fontSize: 18 }}>
                              {PLATFORM_ICONS[chat.platform]?.letter ||
                                chat.client_name?.charAt(0) ||
                                "?"}
                            </Typography>
                          )}
                        </Avatar>
                      </Badge>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <PlatformBadge platform={chat.platform} size={16} />
                          <Typography
                            variant="subtitle2"
                            noWrap
                            sx={{ flex: 1 }}
                          >
                            {chat.client_name || "Неизвестный"}
                          </Typography>
                          {chat.ai_auto_reply && (
                            <SmartToyIcon
                              sx={{ fontSize: 16, color: "success.main" }}
                            />
                          )}
                          {chat.platform === 'web' && !chat.ai_auto_reply && <Chip size="small"
                            color={chat.unread_count > 0 ? 'warning' : 'default'}
                            label={chat.unread_count > 0 ? 'Нужен сотрудник' : 'Сотрудник'} />}
                        </Box>
                      }
                      secondary={
                        <>
                          {chat.item_title && (
                            <Typography
                              variant="caption"
                              display="block"
                              color="primary.main"
                              fontWeight={500}
                              noWrap
                            >
                              📦 {chat.item_title}
                            </Typography>
                          )}
                          <Typography variant="body2" noWrap>
                            {chat.last_message || "Нет сообщений"}
                          </Typography>
                        </>
                      }
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ whiteSpace: "nowrap", ml: 1 }}
                    >
                      {formatTime(chat.last_message_time)}
                    </Typography>
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
              {chats.length === 0 && !loading && (
                <Box sx={{ p: 4, textAlign: "center" }}>
                  <Typography color="text.secondary">Нет чатов</Typography>
                </Box>
              )}
            </List>
          )}

          {totalPages > 1 && (
            <Box
              sx={{
                p: 1,
                display: "flex",
                justifyContent: "center",
                borderTop: 1,
                borderColor: "divider",
              }}
            >
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, v) => setPage(v)}
                size="small"
                color="primary"
              />
            </Box>
          )}
        </ChatListPanel>

        {/* ── Правая панель: окно чата ── */}
        <ChatWindow className="erp-chat-window" elevation={3}>
          {selectedChat ? (
            <>
              <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    <PlatformBadge platform={selectedChat.platform} size={28} />
                    <Box>
                      <Typography variant="h6">
                        {selectedChat.client_name || "Неизвестный"}
                      </Typography>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {selectedChat.client_phone && (
                          <Typography variant="caption" color="text.secondary">
                            <PhoneIcon sx={{ fontSize: 14, mr: 0.5 }} />
                            {selectedChat.client_phone}
                          </Typography>
                        )}
                        <Chip
                          label={
                            PLATFORM_ICONS[selectedChat.platform]?.label ||
                            selectedChat.platform
                          }
                          size="small"
                          sx={{
                            bgcolor:
                              PLATFORM_ICONS[selectedChat.platform]?.color ||
                              "#999",
                            color: "#fff",
                            fontWeight: 600,
                            height: 20,
                            fontSize: 11,
                          }}
                        />
                        {selectedChat.messages && (
                          <Typography variant="caption" color="text.secondary">
                            💬 {selectedChat.messages.length} сообщ.
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                    {!selectedChat.client_id && (
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        startIcon={<PersonAddIcon />}
                      >
                        Создать клиента
                      </Button>
                    )}
                    {selectedChat.client_id && (
                      <Chip
                        label={`Клиент #${selectedChat.client_id}`}
                        color="success"
                        variant="outlined"
                        size="small"
                      />
                    )}
                    {selectedChat.ai_available === false ? (
                      <Chip size="small" label="ИИ отключён" variant="outlined" />
                    ) : <Tooltip
                      title={
                        selectedChat.ai_auto_reply
                          ? "AI-бот ВКЛЮЧЕН ✅"
                          : "AI-бот ВЫКЛЮЧЕН"
                      }
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          bgcolor: selectedChat.ai_auto_reply ? "success.light" : "grey.300",
                          borderRadius: 2,
                          pl: 0.75,
                        }}
                      >
                        <SmartToyIcon
                          sx={{
                            fontSize: 17,
                            color: selectedChat.ai_auto_reply ? "success.dark" : "text.secondary",
                          }}
                        />
                        <Switch
                          checked={selectedChat.ai_auto_reply || false}
                          onChange={(e) => handleToggleAI(e.target.checked)}
                          color="success"
                          size="small"
                          inputProps={{ "aria-label": "Автоответ AI" }}
                        />
                      </Box>
                    </Tooltip>}
                    <Tooltip
                      title={
                        selectedChat.notify_rental !== false
                          ? "Уведомления аренды ВКЛЮЧЕНЫ 🔔"
                          : "Уведомления аренды ВЫКЛЮЧЕНЫ"
                      }
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          bgcolor: selectedChat.notify_rental !== false ? "warning.light" : "grey.300",
                          borderRadius: 2,
                          pl: 0.75,
                        }}
                      >
                        <span style={{ fontSize: 15 }}>🔔</span>
                        <Switch
                          checked={selectedChat.notify_rental !== false}
                          onChange={(e) => handleToggleNotify(e.target.checked)}
                          color="warning"
                          size="small"
                          inputProps={{ "aria-label": "Уведомления аренды" }}
                        />
                      </Box>
                    </Tooltip>
                    {selectedChat.ai_available !== false && <Tooltip title="Ответить с помощью AI (вручную)">
                      <IconButton
                        color="secondary"
                        size="small"
                        onClick={() => handleAIResponse()}
                        disabled={sending || aiGenerating}
                        sx={{
                          bgcolor: "secondary.light",
                          "&:hover": { bgcolor: "secondary.main", color: "white" },
                        }}
                      >
                        {aiGenerating ? <CircularProgress size={20} /> : <SmartToyIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>}
                  </Box>
                </Box>
              </Box>

              <MessagesContainer ref={messagesContainerRef} data-testid="manager-chat-messages">
                {(selectedChat.messages || []).map((msg, idx) => (
                  <MessageBubble key={idx} isOwn={msg.type === "исходящее"}>
                    {msg.type === 'служебное' && <Typography variant="caption" color="text.secondary">Служебная запись · клиенту не показывается</Typography>}
                    {msg.is_ai_generated && msg.type === "исходящее" && (
                      <Typography
                        variant="caption"
                        sx={{
                          opacity: 0.7,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.3,
                          mb: 0.3,
                        }}
                      >
                        <SmartToyIcon sx={{ fontSize: 12 }} /> AI
                      </Typography>
                    )}
                    <Typography variant="body1">{msg.content}</Typography>
                    <Typography
                      variant="caption"
                      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                        flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}
                    >
                      <Box component="span" sx={{ opacity: 0.7 }}>{formatTime(msg.timestamp)}</Box>
                      {selectedChat.platform === 'web' && msg.type === 'исходящее' && (
                        <MessageReceipt message={msg} />
                      )}
                    </Typography>
                  </MessageBubble>
                ))}
              </MessagesContainer>

              <InputContainer>
                <TextField
                  fullWidth
                  placeholder="Введите сообщение..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  disabled={sending}
                  multiline
                  minRows={1}
                  maxRows={5}
                  inputProps={{ maxLength: CHAT_MESSAGE_MAX_LENGTH }}
                  helperText={`${message.length}/${CHAT_MESSAGE_MAX_LENGTH}`}
                  size="small"
                />
                <IconButton
                  color="primary"
                  onClick={handleSendMessage}
                  disabled={!message.trim() || sending}
                >
                  {sending ? <CircularProgress size={24} /> : <SendIcon />}
                </IconButton>
              </InputContainer>
            </>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Typography variant="h6" color="text.secondary">
                Выберите чат для начала общения
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Здесь отображаются чаты со всех платформ: Avito, Telegram и
                другие
              </Typography>
            </Box>
          )}
        </ChatWindow>
      </ChatContainer>
      <Dialog
        open={aiDraftOpen}
        onClose={sending ? undefined : discardAIDraft}
        fullWidth
        maxWidth="sm"
        aria-labelledby="ai-draft-title"
      >
        <DialogTitle id="ai-draft-title">Черновик ответа AI</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Проверьте или измените текст перед отправкой клиенту.
          </Typography>
          <TextField
            label="Текст ответа"
            value={aiDraft}
            onChange={(event) => setAiDraft(event.target.value)}
            fullWidth
            multiline
            minRows={5}
            disabled={sending || aiGenerating}
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: "wrap" }}>
          <Button
            color="error"
            startIcon={<DeleteOutlineIcon />}
            onClick={discardAIDraft}
            disabled={sending || aiGenerating}
          >
            Удалить
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            startIcon={aiGenerating ? <CircularProgress size={16} /> : <AutorenewIcon />}
            onClick={() => handleAIResponse(aiDraftChat)}
            disabled={sending || aiGenerating}
          >
            Перегенерировать
          </Button>
          <Button
            variant="contained"
            onClick={sendAIDraft}
            disabled={!aiDraft.trim() || sending || aiGenerating}
          >
            {sending ? "Отправка..." : "Отправить"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default MessengerPage;
